import type { Edge } from '@xyflow/react';
import type { ConversationTrace } from '@/lib/api';
import type { FlowData, PhaseFlowNode, PhaseStatus } from './types';

// Phase configuration matching agent-trace.tsx
export const PHASE_CONFIG = [
  { key: 'phase1', phaseNumber: 1, name: 'Query Expander', color: 'text-blue-500', iconName: 'zap' as const },
  { key: 'phase2', phaseNumber: 2, name: 'Vector Search', color: 'text-green-500', iconName: 'search' as const },
  { key: 'phase3', phaseNumber: 3, name: 'Assessor', color: 'text-yellow-500', iconName: 'fileSearch' as const },
  { key: 'phase4a', phaseNumber: 4, name: 'Reasoner', color: 'text-purple-500', iconName: 'brain' as const },
  { key: 'phase4b', phaseNumber: 5, name: 'Formatter', color: 'text-pink-500', iconName: 'sparkles' as const },
] as const;

// Model name mapping (without openrouter/ prefix)
const MODEL_NAME_MAP: Record<string, string> = {
  'google/gemini-3.1-flash-lite-preview': 'Gemini 3.1 Flash Lite',
  'google/gemini-3-flash-preview': 'Gemini 3 Flash',
  'deepseek/deepseek-r1': 'DeepSeek R1',
  'deepseek/deepseek-r1-distill-qwen-32b': 'DeepSeek R1 32B',
  'openai/o4-mini': 'o4-mini',
  'openai/gpt-4.1': 'GPT-4.1',
  'openai/gpt-4.1-nano': 'GPT-4.1 Nano',
  'anthropic/claude-haiku-4.5': 'Claude Haiku 4.5',
  'anthropic/claude-sonnet-4.5': 'Claude Sonnet 4.5',
  'amazon/nova-2-lite-v1:free': 'Nova 2 Lite',
  'x-ai/grok-4.1-fast': 'Grok 4.1 Fast',
  'text-embedding-3-small': 'Ada 3 Small',
};

export function getModelShortName(model?: string): string {
  if (!model) return '';
  // Strip 'openrouter/' prefix for lookup
  const sanitizedModel = model.startsWith('openrouter/')
    ? model.slice('openrouter/'.length)
    : model;
  return MODEL_NAME_MAP[sanitizedModel] || sanitizedModel.split('/').pop() || 'Unknown';
}

export function formatDuration(ms?: number): string {
  if (!ms) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// Get phase status from trace data
function getPhaseStatus(phaseKey: string, trace: ConversationTrace): PhaseStatus {
  if (trace.error?.phase === phaseKey) return 'error';

  switch (phaseKey) {
    case 'phase1':
      return trace.phase1 ? 'completed' : 'pending';
    case 'phase2':
      return trace.phase2 ? 'completed' : 'pending';
    case 'phase3':
      return trace.phase3 ? 'completed' : 'pending';
    case 'phase4a':
      return trace.phase4?.data.reasonerAnswer ? 'completed' : 'skipped';
    case 'phase4b':
      // Formatter is completed if there's a formatted response OR if phase4 exists (fallback case)
      return (trace.phase4?.data.formattedResponse || trace.phase4) ? 'completed' : 'pending';
    default:
      return 'pending';
  }
}

// Get model for a phase
function getPhaseModel(phaseKey: string, trace: ConversationTrace): string | undefined {
  switch (phaseKey) {
    case 'phase1':
      return trace.phase1?.model;
    case 'phase2':
      return trace.phase2?.embeddingModel;
    case 'phase3':
      return trace.phase3?.model;
    case 'phase4a':
      return trace.phase4?.reasonerModel;
    case 'phase4b':
      return trace.phase4?.formatterModel;
    default:
      return undefined;
  }
}

// Get duration for a phase
function getPhaseDuration(phaseKey: string, trace: ConversationTrace): number | undefined {
  switch (phaseKey) {
    case 'phase1':
      return trace.phase1?.durationMs;
    case 'phase2':
      return trace.phase2?.durationMs;
    case 'phase3':
      return trace.phase3?.durationMs;
    case 'phase4a': {
      // Estimate reasoner duration based on token ratio
      if (!trace.phase4?.durationMs) return undefined;
      const reasonerTokens = trace.phase4.reasonerOutputTokens || 1;
      const formatterTokens = trace.phase4.formatterOutputTokens || 1;
      return Math.round((trace.phase4.durationMs * reasonerTokens) / (reasonerTokens + formatterTokens));
    }
    case 'phase4b': {
      // Estimate formatter duration based on token ratio
      if (!trace.phase4?.durationMs) return undefined;
      const reasonerTokens = trace.phase4.reasonerOutputTokens || 1;
      const formatterTokens = trace.phase4.formatterOutputTokens || 1;
      return Math.round((trace.phase4.durationMs * formatterTokens) / (reasonerTokens + formatterTokens));
    }
    default:
      return undefined;
  }
}

// Node dimensions and layout
const NODE_HEIGHT = 80;
const NODE_VERTICAL_GAP = 20;
const NODE_X_POSITION = 100; // Center horizontally in the flow area

// Transform API trace data to React Flow format
export function transformTraceToFlowData(
  trace: ConversationTrace | undefined,
  selectedPhase: string | null
): FlowData {
  if (!trace?.hasTrace) {
    return { nodes: [], edges: [], phaseDetails: {} };
  }

  // Create nodes
  const nodes: PhaseFlowNode[] = PHASE_CONFIG.map((phase, index) => ({
    id: phase.key,
    type: 'phaseNode',
    position: {
      x: NODE_X_POSITION,
      y: index * (NODE_HEIGHT + NODE_VERTICAL_GAP),
    },
    data: {
      phaseNumber: phase.phaseNumber,
      phaseName: phase.name,
      model: getPhaseModel(phase.key, trace),
      durationMs: getPhaseDuration(phase.key, trace),
      status: getPhaseStatus(phase.key, trace),
      isSelected: phase.key === selectedPhase,
      iconName: phase.iconName,
      iconColor: phase.color,
    },
  }));

  // Create edges connecting phases sequentially
  const edges: Edge[] = [
    { id: 'e1-2', source: 'phase1', target: 'phase2', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'e2-3', source: 'phase2', target: 'phase3', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'e3-4a', source: 'phase3', target: 'phase4a', animated: true, style: { stroke: '#94a3b8' } },
    { id: 'e4a-4b', source: 'phase4a', target: 'phase4b', animated: true, style: { stroke: '#94a3b8' } },
  ];

  return { nodes, edges, phaseDetails: {} };
}
